import { useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import { BrandMark, escapeOwnedByOverlay, Icon, useBodyScrollLock } from "@cntrl/ui";
import { ThreadList } from "./ThreadList.js";
import { ThreadView, type DraftPerson } from "./ThreadView.js";
import { NewThreadModal } from "./NewThreadModal.js";
import { useMessageable, useThreads } from "../../app/hooks/useInbox.js";
import { useMe } from "../../app/hooks/useSession.js";
import { canCreateGroupChat } from "../../app/auth/session.js";
import { directCounterpartIds } from "../../app/hooks/inboxFormat.js";
import type { ShowLinkResolver } from "../../app/showLink.js";
import styles from "./Inbox.module.scss";
import { usePhoneLayout } from "../../app/phoneLayout.js";

export interface InboxScreenProps {
  /** The open thread (rides the URL: `/messages/:threadId`). Undefined = no thread selected. */
  selectedThreadId?: string;
  /** Navigate to a thread (→ `/messages/:threadId`). */
  onSelectThread: (threadId: string) => void;
  /** Close the inbox modal (→ the dashboard). */
  onClose: () => void;
  /** Mobile: back from a thread page to the list (→ `/messages`). */
  onBack?: () => void;
  /**
   * Desktop: deselect the open conversation, leaving the overlay up (C-Esc's first layer).
   *
   * It is the CALLER's because only the caller knows where the selection lives: the overlay keeps it in
   * component state, and the routed page keeps it in the URL. Absent means there is nothing to deselect, so
   * Escape falls straight through to closing.
   */
  onCloseThread?: () => void;
  /**
   * Open a conversation with this PERSON, once (C-Entry) — a "Message" action somewhere else in the product
   * asking for a thread by who is in it rather than by its id.
   *
   * A person and not a thread, because "which conversation is that" is answered HERE, by the same
   * `existingDmByPerson` the picker already consults: an existing history opens, and anyone else becomes a
   * draft that writes nothing until there is something to say. A caller resolving it would be a second
   * implementation of that rule.
   */
  openWithPersonId?: string;
  /** Tell the owner the request has been taken, so it fires once rather than on every render. */
  onOpenedWithPerson?: () => void;
  /**
   * Where a show chat's SHOW lives, from the shell doing the rendering (ART-159, C-Mobile). The inbox cannot
   * work it out: it opens from the topbar, outside the artist shell's own `BandProvider`, so context cannot
   * even tell the two shells apart — see `showLink.ts`.
   */
  showHref?: ShowLinkResolver;
  /** Go somewhere in the app — used only by the Day of Show link. */
  onNavigate?: (route: string) => void;
  /**
   * This inbox IS a route (`/messages…`), not the topbar overlay. Leaving it by navigating then needs no close
   * first — the destination route unmounts it — and must not have one: a routed close is a history-BACK, which
   * is asynchronous, so it landed after the navigation and undid it (the Day of Show link took you straight
   * back to the inbox).
   */
  routed?: boolean;
}

/**
 * The unified messages inbox (shot-48) — a two-pane MODAL on desktop, full-screen panes on mobile. Left:
 * the thread list (title, "+ New" pill, search, grouped rows). Right: the selected conversation, or the
 * no-thread-selected empty state (a centered CNTRL mark, and nothing else). Person-
 * scoped: no <RequireBand> for the list, and none is needed — "+ New" picks a PERSON from
 * core.messageable_people, which keys on the session person alone (PO, 2026-09-11: a band is not a
 * correspondent, so there is nothing here that needs a band mounted).
 */
export function InboxScreen({
  selectedThreadId,
  onSelectThread,
  onClose,
  onBack,
  onCloseThread,
  openWithPersonId,
  onOpenedWithPerson,
  showHref,
  onNavigate,
  routed = false,
}: InboxScreenProps) {
  // Aligns with the shell swap: `ShellLayout` mounts `MobileShell` below `lg` (900), so the inbox must
  // present its mobile page/thread layout across the whole `< lg` range. Keyed on `md` (720) before, the
  // 720-899 band rendered the desktop two-pane MODAL over the mobile tab bar (ART-153 pass two).
  const isDesktop = !usePhoneLayout();
  const [newOpen, setNewOpen] = useState(false);
  // A conversation being composed with somebody for the first time. It lives HERE, in component state, and
  // deliberately not in the URL: a draft has no id to put there, and it is meant to evaporate — navigating
  // to a real thread clears it below, and closing the inbox unmounts it (ART-159, PO: an abandoned DM
  // "should go away when you navigate away").
  const [draft, setDraft] = useState<DraftPerson | undefined>(undefined);
  const panelRef = useRef<HTMLDivElement>(null);
  const threads = useThreads();
  // The picker hands back an ID; a DRAFT needs the NAME and face to head the pane before any round trip.
  // Same query key the picker reads, so this is the cache rather than a second request.
  const network = useMessageable();
  const me = useMe();

  const showChat = !!selectedThreadId || !!draft;
  // On a mobile LIST the inbox is an in-flow tab PAGE (bottom navbar visible, no scrim, no ✕), NOT an
  // overlay — the same "ordinary tab destination" `isFullScreenRoute` already exempts. It is only a
  // `position: fixed` overlay on desktop and on the mobile THREAD takeover, and only THOSE need the body
  // frozen behind them — a wheel over the backdrop otherwise scrolls the page underneath. The list page
  // scrolls its own rows, so locking the page there would freeze the very thing the reader is scrolling.
  const mobileListPage = !isDesktop && !showChat;
  useBodyScrollLock(!mobileListPage);

  // Moving to a DIFFERENT thread ends the draft — including the one the draft just became. It fires on a
  // CHANGE, so it covers back/forward and a deep link; picking a row is handled by `selectThread` below,
  // which also covers re-selecting the thread already in the URL (no change, so no effect).
  useEffect(() => {
    if (selectedThreadId) setDraft(undefined);
  }, [selectedThreadId]);

  const messageableById = useMemo(() => {
    const map = new Map<string, DraftPerson>();
    for (const p of network.data?.people ?? []) {
      if (p.personId) {
        map.set(p.personId, {
          personId: p.personId,
          displayName: p.displayName ?? "",
          avatarMediaId: (p.avatarMediaId as string | null) ?? null,
        });
      }
    }
    return map;
  }, [network.data]);

  /** Open a real thread, and end any draft — the two are one gesture from the reader's side. */
  function selectThread(threadId: string) {
    setDraft(undefined);
    onSelectThread(threadId);
  }

  // The conversation this person ALREADY has with me, if the loaded page holds it. Picking somebody you are
  // already talking to must open that history rather than a blank draft — and the list is a page, so on a
  // very long inbox this can miss and fall through to a draft. That degrades correctly: the first message
  // get-or-creates, so it lands in the existing thread and the view routes to it.
  const myPersonId = me.data?.person?.personId;
  const existingDmByPerson = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of threads.data?.threads ?? []) {
      if (t.kind !== "direct" || !t.threadId) continue;
      for (const id of directCounterpartIds(t, myPersonId)) map.set(id, t.threadId as string);
    }
    return map;
  }, [threads.data, myPersonId]);

  /**
   * SOMEBODY ASKED TO MESSAGE A PERSON (C-Entry). Resolved the same way the picker resolves a pick: an
   * existing conversation OPENS, anyone else becomes a DRAFT. One rule, one place — a caller that resolved
   * the thread itself would be a second implementation of it.
   *
   * **It waits for the two reads it depends on.** `existingDmByPerson` is built from the thread list and
   * the draft's name and face come from the messageable list, so firing before either has landed would open
   * a draft for somebody you are already talking to, or head the pane with a blank name. The effect re-runs
   * as they arrive and `onOpenedWithPerson` is what stops it firing twice.
   */
  useEffect(() => {
    if (!openWithPersonId) return;
    if (threads.isLoading || network.isLoading) return;
    const existing = existingDmByPerson.get(openWithPersonId);
    if (existing) {
      setDraft(undefined);
      onSelectThread(existing);
    } else {
      const person = messageableById.get(openWithPersonId);
      // Not messageable (or not loaded): the inbox still OPENS, on the list. The alternative is a press
      // that appears to do nothing — and the surface that offered the action already gates on the same
      // list, so this is the belt rather than the braces.
      if (person) setDraft(person);
    }
    onOpenedWithPerson?.();
  }, [
    openWithPersonId,
    threads.isLoading,
    network.isLoading,
    existingDmByPerson,
    messageableById,
    onSelectThread,
    onOpenedWithPerson,
  ]);

  /**
   * TWO ESCAPE LAYERS (ART-159, C-Esc). The first closes the open CONVERSATION and leaves the overlay up;
   * the second closes the overlay. It used to be one, which threw away both at once — and the conversation
   * is the thing you just opened, so the layer a reader means by Escape is almost always that one.
   *
   * **DESKTOP ONLY, and that is not a shortcut.** On mobile the two panes are one at a time: closing the
   * conversation IS going back to the list, which the back affordance already does and which the device's
   * own back gesture also does. A first-Escape layer there would be a third route to the same place, and
   * the first press would appear to do nothing on a device with no visible keyboard.
   *
   * **A nested dialog owns Escape while it is up** — the "+ New" picker, the edit and members dialogs —
   * or the whole overlay would close out from under it. The modals in `@cntrl/ui` stop the event at their
   * own layer; `newOpen` is the one this component raises itself, so it is the one it has to know about.
   * Those primitives do NOT stop the event, though — they answer it topmost-only within their own stack, which
   * this listener is outside — so it also stands down whenever that stack holds anything (a profile, the edit or
   * members dialog). Without that, one Escape closed a profile AND the conversation under it (ART-264).
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || newOpen || escapeOwnedByOverlay()) return;
      if (isDesktop && (selectedThreadId || draft)) {
        setDraft(undefined);
        onCloseThread?.();
        return;
      }
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onCloseThread, newOpen, isDesktop, selectedThreadId, draft]);

  // On mobile only one pane shows: the thread page when one is selected, else the list.
  const showList = isDesktop || !showChat;
  const showChatPane = isDesktop || showChat;

  // The "+ New" picker — shared by the overlay and the mobile list PAGE, so its resolve rule (existing DM
  // opens, anyone else becomes a draft) lives in exactly one place.
  const newThreadModal = newOpen ? (
    <NewThreadModal
      onClose={() => setNewOpen(false)}
      // The SERVED verdict decides whether the picker takes more than one person — not a seat this
      // component reads, and not a second button. The create route refuses independently.
      canCreateGroup={canCreateGroupChat(me.data)}
      onPickedOne={(personId) => {
        setNewOpen(false);
        const existing = existingDmByPerson.get(personId);
        // Already talking to them → open the history. Otherwise a DRAFT, which writes nothing until
        // there is something to say.
        if (existing) onSelectThread(existing);
        else {
          const person = messageableById.get(personId);
          if (person) setDraft(person);
        }
      }}
      // Straight into the chat that was just made. A group EXISTS the moment it is created (unlike a DM
      // draft), so there is a real id to open — and landing back on the list would make the reader hunt
      // for the thing they just named.
      onCreatedGroup={(threadId) => selectThread(threadId)}
    />
  ) : null;

  // MOBILE LIST = AN IN-FLOW TAB PAGE (bottom navbar visible, no scrim, no ✕). It renders in the shell's
  // content area, so the shell's header + bottom tab bar stay put and the Inbox tab reads as active — the way
  // every other bottom-nav destination behaves. The ✕ used to survive here "because the list has no back
  // arrow"; the tab bar IS the way out now, so it goes.
  if (mobileListPage) {
    return (
      <>
        {/* NOT `.listPaneFull` — that class exists to reserve header space for the overlay's ✕ (via a var only
            the overlay defines), which zeroes the right padding here and bumps "+ New" to the edge. */}
        <div className={`${styles.listPane} ${styles.listPage}`}>
          <ThreadList
            selectedThreadId={selectedThreadId}
            onSelect={selectThread}
            onNewThread={() => setNewOpen(true)}
            compact // ART-186: no title/eyebrow on the mobile page (the shell header carries "Inbox"), search + New on one line
          />
        </div>
        {newThreadModal}
      </>
    );
  }

  return (
    <div className={isDesktop ? styles.backdrop : `${styles.backdrop} ${styles.backdropFull}`}>
      <button type="button" className={styles.scrim} aria-label="Close messages" onClick={onClose} />
      <div
        ref={panelRef}
        className={isDesktop ? styles.panel : `${styles.panel} ${styles.panelMobile}`}
        role="dialog"
        aria-modal="true"
        aria-label="Messages"
      >
        {/* NO PER-THREAD ✕ ON THE TAKEOVER (ART-159, C-Mobile) — the handoff's own words, and the reason is
            visible the moment you open it on a phone: the thread draws its own back arrow at the leading
            edge, so a ✕ at the trailing edge is a second dismiss that goes somewhere else (back to the page
            you came from, rather than to the list). It also sits exactly where the spec puts the header's
            fourth slot.

            It SURVIVES on the mobile LIST, which has no back arrow of its own — a deep link to `/messages`
            would otherwise be a screen with no way out but the tab bar. */}
        {(isDesktop || !showChat) && (
          <button type="button" className={styles.closeCorner} aria-label="Close messages" onClick={onClose}>
            <Icon icon={X} size={20} />
          </button>
        )}

        {showList && (
          <div className={isDesktop ? styles.listPane : `${styles.listPane} ${styles.listPaneFull}`}>
            <ThreadList
              selectedThreadId={draft ? undefined : selectedThreadId}
              onSelect={selectThread}
              onNewThread={() => setNewOpen(true)}
            />
          </div>
        )}

        {showChatPane &&
          (showChat ? (
            // A DRAFT WINS OVER WHATEVER IS IN THE URL, and getting that backwards is what broke starting a
            // DM at all: this used to read `draft={selectedThreadId ? undefined : draft}`, so picking
            // somebody while a conversation was open set the draft in state and then rendered the OLD
            // thread over it. The picker closed, the header did not change, and nothing said why — and
            // since you are usually reading a conversation when you decide to write to someone else, that
            // was the normal path rather than an edge. Passing `threadId` as undefined alongside it keeps
            // the two from both being live: there is one subject, and the draft is it.
            <ThreadView
              threadId={draft ? undefined : selectedThreadId}
              draft={draft}
              onDraftStarted={(threadId) => {
                setDraft(undefined);
                onSelectThread(threadId);
              }}
              onBack={isDesktop ? undefined : draft ? () => setDraft(undefined) : onBack}
              showHref={showHref}
              // LEAVING CLOSES THE OVERLAY FIRST. The topbar overlay is `position: fixed` over the whole viewport,
              // so navigating underneath it lands you on the show with the conversation still covering it.
              // ThreadView says WHERE to go; knowing the overlay has to get out of the way is this component's
              // business, because it is the thing in the way. A ROUTED inbox skips it: the navigation itself
              // leaves the route, and its close is a history-back that would land afterwards and undo it.
              onNavigate={
                onNavigate
                  ? (route) => {
                      if (!routed) onClose();
                      onNavigate(route);
                    }
                  : undefined
              }
            />
          ) : (
            <div className={styles.chatPane}>
              {/* The mark ALONE (PO, 2026-09-11). The headline and sub-copy explained a pane whose meaning
                  the list beside it already carries, and this is a state a reader passes through on the way
                  to a thread rather than one they sit in. BrandMark names itself (`role="img"`), so the pane
                  is still announced as something rather than as an empty box. */}
              <div className={styles.emptyPane}>
                <BrandMark size={48} className={styles.emptyMark} />
              </div>
            </div>
          ))}
      </div>

      {newThreadModal}
    </div>
  );
}
